import React, { useState, useEffect } from "react";
import useAPI_NoTimeOut from "../hooks/use-api-noTimeOut";
import useAccessToken from "../hooks/use-accesstoken";
import { useTheme } from "@emotion/react";
import { 
    Box, 
    Typography, 
    Button, 
    TextField, 
    IconButton, 
    Paper, 
    CircularProgress,
    Checkbox,
    FormControlLabel,
    Radio,
    RadioGroup,
    FormControl,
    Chip,
    LinearProgress,
} from "@mui/material";
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import DownloadIcon from '@mui/icons-material/Download';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';

const ConfigurationPanel = () => {
    const theme = useTheme();
    const { api } = useAPI_NoTimeOut();
    const { accessToken } = useAccessToken();

    const styles = {
        container: {
            backgroundColor: theme.palette.primary.light,
            color: "#fff",
            height: "100%",
            display: "flex",
            flexDirection: "column",
            padding: "20px",
            boxSizing: "border-box",
            overflowY: "auto",
            width: "100%",
            maxWidth: "1200px",
            margin: "0 auto",
        },
        formContainer: {
            display: "flex",
            flexDirection: "column",
            gap: "15px",
        },
        inputPair: {
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            width: "100%",
        },
        inputWrapper: {
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
        },
        input: {
            padding: "12px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            minWidth: "150px",
            fontSize: "16px",
            height: "40px",
            transition: "all 0.3s ease",
            width: "100%",
            resize: "none",
        },
        inputExpandedHeight: {
            padding: "18px",
            minWidth: "200px",
            fontSize: "12px",
            height: "300px",
            resize: "none",
            overflow: "auto",
        },
        redButton: {
            cursor: "pointer",
            backgroundColor: "#ff4d4d",
            color: "#fff",
            border: "none",
            borderRadius: "8px",
            padding: "8px 15px",
            fontSize: "14px",
        },
        greenButton: {
            backgroundColor: "#4caf50",
            marginTop: "15px",
            padding: "12px 25px",
            textAlign: "center",
            cursor: "pointer",
            border: "none",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "16px",
            transition: "background-color 0.3s ease",
        },
        scrollableContainer: {
            display: "flex",
            flexDirection: "column",
            overflowX: "auto",
            overflowY: "auto",
            maxHeight: "450px",
        },
        label: {
            display: "block",
            marginBottom: "5px",
            color: theme.palette.primary.dark
        },
        minusButtonWrapper: {
            marginLeft: "10px",
            display: "flex",
            alignItems: "center",
        },
        fileList: {
            marginTop: "20px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
        },
        fileItem: {
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "8px 15px",
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            border: "1px solid #ccc",
            borderRadius: "8px",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        },
        deleteButton: {
            backgroundColor: "#ff4d4d",
            color: "#fff",
            border: "none",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            fontSize: "12px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            cursor: "pointer",
        },
        uploadButton: {
            backgroundColor: "#4caf50",
            padding: "8px 18px",
            textAlign: "center",
            cursor: "pointer",
            border: "none",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "14px",
            display: "inline-block",
            transition: "background-color 0.3s ease",
        },
        customFileInput: {
            position: "relative",
            overflow: "hidden",
            display: "inline-block",
            width: "auto",
        },
        fileInput: {
            position: "absolute",
            top: "0",
            right: "0",
            margin: "0",
            padding: "0",
            fontSize: "16px",
            cursor: "pointer",
            opacity: 0,
            width: "100%",
            height: "100%",
        },
        customFileButton: {
            backgroundColor: "#4caf50",
            padding: "8px 18px",
            cursor: "pointer",
            border: "none",
            color: "#fff",
            borderRadius: "8px",
            fontSize: "14px",
            display: "inline-block",
            width: "auto",
            textAlign: "center",
            transition: "background-color 0.3s ease",
        },
        buttonWrapper: {
            display: "flex",
            gap: "15px",
            marginTop: "15px",
        },
        separationLine: {
            margin: "20px 0",
            borderTop: "1px solid #ccc",
        },
        fileNameWrapper: {
            position: "relative",
            display: "inline-block",
            padding: "8px",
        },
        buttonPressed: {
            backgroundColor: "#388e7f",
        },
        buttonHover: {
            backgroundColor: "#388e3c",
        },
        dateWrapper: {
            display: "flex",
            flexDirection: "column",
            gap: "10px",
        },
        dateInputWrapper: {
            display: "flex",
            justifyContent: "space-between",
            gap: "20px",
        },
        dateInput: {
            padding: "8px",
            height: "30px",
            fontSize: "14px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            width: "130px",
            textAlign: "center",
        },
        checkboxGroup: {
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: "20px",
        },
        checkboxLabel: {
            display: "flex",
            alignItems: "center",
            gap: "10px",
            color: theme.palette.primary.dark
        },
        radioGroup: {
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: "20px",
        },
        radioLabel: {
            color: theme.palette.primary.dark
        },
        subjectPopup: {
            position: "absolute",
            top: "0",
            left: "50%",
            transform: "translateX(-50%)",
            padding: "10px 20px",
            borderRadius: "8px",
            backdropFilter: "blur(4px)",
            zIndex: 100,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            minWidth: "250px",
            marginBottom: "10px"
        },
        dropdown: {
            width: "100%",
            padding: "8px",
            borderRadius: "4px",
            border: "1px solid #ccc",
            fontSize: "16px",
            marginBottom: "10px",
        },
        popupButtonWrapper: {
            display: "flex",
            justifyContent: "center",
            gap: "15px",
            width: "100%",
        },

        sourceTag: {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.dark,
            borderRadius: "50px",
            padding: "4px 8px",
            cursor: "pointer",
            fontSize: "12px",
            whiteSpace: "nowrap",
            marginRight: "5px",
            marginBottom: "5px",
            transition: "all 0.2s ease",
            border: `2px solid ${theme.palette.primary.dark}`,
        },
        sourceTagActive: {
            backgroundColor: theme.palette.primary.dark,
            color: theme.palette.primary.light,
        },
    };

    const [inputFields, setInputFields] = useState(() => {
        const storedFields = localStorage.getItem('reportInputFields');
        return storedFields ? JSON.parse(storedFields) : [
            { subject: "", description: "", examples: "", sources: [] }
        ];
    });
    const [focusedInput, setFocusedInput] = useState(null);
    const [uploadedFiles, setUploadedFiles] = useState(() => {
        const storedFiles = localStorage.getItem('reportUploadedFiles');
        return storedFiles ? JSON.parse(storedFiles) : [];
    });
    const [fileInput, setFileInput] = useState(null);
    const [fromDate, setFromDate] = useState(() => {
        return localStorage.getItem('reportFromDate') || "";
    });
    const [toDate, setToDate] = useState(() => {
        return localStorage.getItem('reportToDate') || "";
    });

    const [buttonPressed, setButtonPressed] = useState({
        addButton: false,
        fileButton: false,
        uploadButton: false,
    });

    const [reportGeneratingLoading, setReportGeneratingLoading] = useState(false);
    const [reportId, setReportId] = useState(() => {
        // Initialize reportId from localStorage if it exists
        const storedReportId = localStorage.getItem('reportId');
        return storedReportId || null;
    });
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [hoveredButtonIndex, setHoveredButtonIndex] = useState(null);
    const [downloadMessage, setDownloadMessage] = useState("");
    const [checkboxGroup, setCheckboxGroup] = useState(() => {
        const storedCheckboxes = localStorage.getItem('reportCheckboxGroup');
        return storedCheckboxes ? JSON.parse(storedCheckboxes) : {
            "Aktuellt hos oss (internt)": false,
            "Nyheter (internt)": false,
            "Affärsplan (internt)": false,
            "Trygghets arbete (publikt)": false,
            "Nyheter helsingborgshem.se": false,
            "Mynewsdesk": false,
        };
    });

    const [selectedFormat, setSelectedFormat] = useState(() => {
        return localStorage.getItem('reportSelectedFormat') || "PDF";
    });
    const [subjectModalVisible, setSubjectModalVisible] = useState(false);
    const [pendingFile, setPendingFile] = useState(null);
    const [selectedSubjectForPendingFile, setSelectedSubjectForPendingFile] = useState("");
    const [subjectError, setSubjectError] = useState("");

    const [generationProgress, setGenerationProgress] = useState({
        progress: 0,
        completedFiles: 0,
        totalFiles: 0,
        currentStage: "Preparing",
        currentSubject: "",
        completedSubjects: []
    });
    const [isCheckingProgress, setIsCheckingProgress] = useState(false);
    const [progressCheckInterval, setProgressCheckInterval] = useState(null);
    const [isCancelling, setIsCancelling] = useState(false);

    // Function to clear all report data from localStorage
    const clearReportDataFromStorage = () => {
        localStorage.removeItem('reportId');
        localStorage.removeItem('reportInputFields');
        localStorage.removeItem('reportUploadedFiles');
        localStorage.removeItem('reportFromDate');
        localStorage.removeItem('reportToDate');
        localStorage.removeItem('reportCheckboxGroup');
        localStorage.removeItem('reportSelectedFormat');
    };

    // Function to save all report data to localStorage
    const saveReportDataToStorage = () => {
        localStorage.setItem('reportInputFields', JSON.stringify(inputFields));
        localStorage.setItem('reportUploadedFiles', JSON.stringify(uploadedFiles));
        localStorage.setItem('reportFromDate', fromDate);
        localStorage.setItem('reportToDate', toDate);
        localStorage.setItem('reportCheckboxGroup', JSON.stringify(checkboxGroup));
        localStorage.setItem('reportSelectedFormat', selectedFormat);
    };

    const handleGenerateForFramvagnen = () => {
        const defaultInputFields = [
            {
                subject: "Trygghet",
                description: `Klassificera innehåll under denna kategori om det handlar om initiativ, aktiviteter och insatser relaterade till trygghet och trivsel i Helsingborgshems bostadsområden. Helsingborgshem, som det största bostadsbolaget i Helsingborg, har ett stort ansvar för att säkerställa att hyresgästerna känner sig trygga i sina hem och bostadsområden. 
            Som allmännyttigt bolag arbetar vi aktivt både med att skapa säkra fysiska miljöer och att minska utanförskapet och stärka svaga grupper i samhället - det räcker inte med att bygga och förvalta. Helsingborgshem arbetar kontinuerligt med trygghet genom flera strategier som utgår ifrån att vi jobbar förebyggande både med den fysiska miljön, med kontrollen i ett område och med människorna som bor där.`,
                examples: `Vi har sex utgångspunkter för ett effektivt trygghetsarbete: aktuell lägesbild och orsaksanalys, den fysiska miljöns utformning, en god förvaltning av våra hus och områden, social resistens (det handlar om att skapa motståndskraft genom att bygga framtidstro och bidra till inkludering i samhället, till exempel arbetsmarknadsåtgärder för dem som bor i området så att fler utbildar sig och, eller, får ett arbete.), strukturerade samverkan med andra aktörer samt lokalt närvarande polis. 
            1. Närvaro i bostadsområden: Helsingborgshems medarbetare vistas regelbundet i områdena, både dagtid, kvällar och helger, för att skapa trygghet och öka dialogen med hyresgästerna. Helsingborgshem gör även trygghetsvandringar. 
            2. Områdesvärdar och lokalt stöd: Områdesvärdarna arbetar aktivt med barn och unga i bostadsområdena för att bygga relationer och stärka samhörighet, de gör vandringar och aktiviteter i områdena och på våra områdeskontor och mötesplatser @plantan och  @soder. 
            3. Trygghetscertifiering: Bolaget genomför fysiska trygghetsåtgärder - boendemiljön, både inne och ute, ska vara utformad på ett sätt som minskar brottslighet och ökar trygghet. Helsingborgshem genomför ett paket av åtgärder för att göra det tryggare. 
            4. Arbetet mot olovlig andrahandsuthyrning: Helsingborgshem tar fram åtgärder för att säkerställa att lägenheter används enligt regler och att boendemiljön är trygg och rättvis. 
            5. Samverkan med polis och myndigheter: Initiativ såsom Operation Klöver där Helsingborgshem samarbetar med polisen och andra aktörer för att förebygga kriminalitet och bedrägerier. 
            6. Trivsel- och trygghetsskapande aktiviteter: Helsingborgshem arrangerar områdesdagar, fikor, firanden och möten med hyresgäster för att stärka gemenskap och trivsel.`,
                sources: [
                    "Aktuellt hos oss (internt)",
                    "Nyheter (internt)",
                    "Trygghets arbete (publikt)",
                    "Nyheter helsingborgshem.se",
                    "Mynewsdesk"]
            },
            {
                subject: "Kundnöjdhet",
                description: `Klassificera innehåll under denna kategori om det handlar om initiativ och insatser för att förbättra kundupplevelsen och hyresgästernas nöjdhet. Helsingborgshem har som mål att minst 75 procent av kunderna ska vara nöjda med sitt boende och servicen de får.`,
                examples: `Helsingborgshem arbetar för att öka kundnöjdheten genom:
            1. Förbättring av service och bemötande: Helsingborgshem strävar efter att ge snabb och effektiv hjälp via kundtjänst och digitala kanaler. 
            2. Digitalisering och tillgänglighet: Införandet av nya digitala tjänster såsom den förbättrade hemsidan och "Mina Sidor" samt chatboten Thyra för snabbare kundhantering. 
            3. Kundundersökningar och feedback: Bolaget använder verktyg som Kundbarometern och Panelista för att analysera kunders åsikter och förbättra servicen. Vi har också olika former av dialog där vi pratar med våra kunder för att vi är nyfikna på vad de tycker och tänker. 
            4. Boendedialog och evenemang: Helsingborgshem anordnar möten, fikor och evenemang som Drottninghögsdagen för att lyssna på hyresgästerna och skapa engagemang. 
            5. Medarbetarengagemang: Genom interna medarbetarundersökningar såsom WinningTemp mäter Helsingborgshem kontinuerligt sina medarbetares arbetsmiljö och engagemang, hur stor andel ambassadörer företaget har, vilket indirekt påverkar kundnöjdheten.`,
                sources: [
                    "Aktuellt hos oss (internt)",
                    "Nyheter (internt)",
                    "Affärsplan (internt)",
                    "Nyheter helsingborgshem.se",
                    "Mynewsdesk"]
            },
            {
                subject: "Lönsamhet",
                description: `Klassificera innehåll under denna kategori om det handlar om insatser för att öka intäkter och optimera kostnader i Helsingborgshem. Helsingborgshem har ett långsiktigt mål att förbättra bolagets resultat och ekonomiska hållbarhet.`,
                examples: `Helsingborgshem arbetar för att stärka lönsamheten genom:
            1. Effektivare drift och kostnadskontroll: Genom att utföra fler tjänster internt istället för att anlita externa entreprenörer minskar kostnaderna.
            2. Lägenhetsunderhåll och kostnadseffektivitet: Helsingborgshem implementerar kostnadseffektiva åtgärder, exempelvis att måla istället för att tapetsera.
            3. Optimering av avtal och leverantörssamarbeten: Helsingborgshem ser över sina leverantörsavtal för att säkerställa hög kvalitet till bästa pris.
            4. Minskning av vakanser: Genom att arbeta strategiskt för att minska vakanta lägenheter säkerställer Helsingborgshem stabila hyresintäkter.`,
                sources: [
                    "Aktuellt hos oss (internt)",
                    "Nyheter (internt)",
                    "Affärsplan (internt)"]
            },
            {
                subject: "Digitalisering",
                description: `Klassificera innehåll under denna kategori om det handlar om Helsingborgshems satsningar på digitala lösningar för att förbättra verksamheten och kundupplevelsen. Helsingborgshem arbetar med digitalisering för att kunna ge snabbare svar och bli mer tillgängliga, tillgängliggöra relevant data och information samt möjliggöra för individuella kundlösningar.`,
                examples: `Helsingborgshem arbetar med digitalisering genom: 
            1. Smarta fastighetssystem: Implementering av nya system såsom Vitec för att förbättra hyresgästernas digitala upplevelse men också för att kunna följa och effektivisera arbetet i våra fastigheter med sådant som vatten, värme och ventilation. 
            2. Digitala självbetjäningstjänster: Utveckling av "Mina Sidor", chatboten Thyra och andra digitala verktyg för att effektivisera kundhantering. 
            3. IT-säkerhet och trygg digital hantering: Helsingborgshem arbetar kontinuerligt med cybersäkerhet och utbildning av medarbetare för att skydda data och system.`,
                sources: [
                    "Aktuellt hos oss (internt)",
                    "Nyheter (internt)",
                    "Affärsplan (internt)",
                    "Nyheter helsingborgshem.se",
                    "Mynewsdesk"]
            },
            {
                subject: "Klimat",
                description: `Klassificera innehåll under denna kategori om det handlar om Helsingborgshem arbete för att minska sin klimatpåverkan och bidra till Helsingborgs klimatneutralitetsmål 2030.`,
                examples: `Helsingborgshem arbetar med klimatfrågor genom:
            1. Hållbart byggande och materialåterbruk: Bolaget ställer höga krav på leverantörer för klimatsmarta byggprojekt och återanvändning av material.
            2. Minskad energiförbrukning och utsläpp: Helsingborgshem samarbetar med CCS för att minska utsläppen från fjärrvärmeanvändning.
            3. Prisbelönta hållbarhetsinitiativ: Projekten får ofta utmärkelser för återbruk och hållbar byggteknik.
            4. Effektivisera och minska avfall: Helsingborgshem arbetar för att minska avfallet och öka återvinningen i sina fastigheter.`,
                sources: [
                    "Aktuellt hos oss (internt)",
                    "Nyheter (internt)",
                    "Affärsplan (internt)",
                    "Nyheter helsingborgshem.se",
                    "Mynewsdesk"]
            },
        ];
        setInputFields(defaultInputFields);

        // Set the date range to last year's January 1st to December 31st.
        const lastYear = new Date().getFullYear() - 1;
        setFromDate(`${lastYear}-01-01`);
        setToDate(`${lastYear}-12-31`);

        // Mark all checkboxes as true.
        setCheckboxGroup({
            "Aktuellt hos oss (internt)": true,
            "Nyheter (internt)": true,
            "Affärsplan (internt)": true,
            "Trygghets arbete (publikt)": true,
            "Nyheter helsingborgshem.se": true,
            "Mynewsdesk": true,
        });
    };

    const handleCheckboxChange = (label) => {
        // Update the checkbox state
        const newCheckboxState = !checkboxGroup[label];
        setCheckboxGroup(prevState => ({
            ...prevState,
            [label]: newCheckboxState
        }));
        
        // If unchecking a source, remove it from all subjects' sources
        if (!newCheckboxState) {
            const updatedFields = inputFields.map(field => {
                // If the field has sources and includes the unchecked label, remove it
                if (field.sources && field.sources.includes(label)) {
                    return {
                        ...field,
                        sources: field.sources.filter(source => source !== label)
                    };
                }
                return field;
            });
            setInputFields(updatedFields);
        }
    };

    const handleAddInputPair = () => {
        setInputFields([...inputFields, { subject: "", description: "", examples: "", sources: [] }]);
    };

    const handleRemoveInputPair = (index) => {
        const updatedPairs = inputFields.filter((_, i) => i !== index);
        setInputFields(updatedPairs);
    };

    const handleInputChange = (index, field, value) => {
        const updatedPairs = [...inputFields];
        updatedPairs[index][field] = value;
        setInputFields(updatedPairs);
    };

    const handleFocus = (index, field) => {
        setFocusedInput({ index, field });
    };

    const handleBlur = () => {
        setFocusedInput(null);
    };

    const isFocused = (index, field) => {
        return focusedInput && focusedInput.index === index && focusedInput.field === field;
    };

    const handleFileChange = (e) => {
        setFileInput(e.target.files[0]);
    };

    const handleFileUpload = () => {
        if (fileInput) {
            setPendingFile(fileInput);
            setSubjectModalVisible(true);
        }
    };

    const handleConfirmFileMapping = () => {
        if (!selectedSubjectForPendingFile) {
            setSubjectError("Vänligen välj ett ämne");
            return;
        }
        setSubjectError("");
        setUploadedFiles([...uploadedFiles, { file: pendingFile, subject: selectedSubjectForPendingFile }]);
        setPendingFile(null);
        setSelectedSubjectForPendingFile("");
        setSubjectModalVisible(false);
        setFileInput(null);
    };

    const handleCancelFileMapping = () => {
        setPendingFile(null);
        setSelectedSubjectForPendingFile("");
        setSubjectModalVisible(false);
    };

    const handleDeleteFile = (item) => {
        setUploadedFiles(uploadedFiles.filter((f) => f !== item));
    };

    const handleButtonPress = (button) => {
        setButtonPressed((prevState) => ({
            ...prevState,
            [button]: true,
        }));
        setTimeout(() => {
            setButtonPressed((prevState) => ({
                ...prevState,
                [button]: false,
            }));
        }, 300);
    };

    const handleButtonHover = (index) => {
        setHoveredButtonIndex(index);
    };

    const handleFromDateChange = (e) => {
        setFromDate(e.target.value);
        if (toDate && new Date(e.target.value) > new Date(toDate)) {
            setToDate("");
        }
    };

    const handleToDateChange = (e) => {
        setToDate(e.target.value);
        if (fromDate && new Date(e.target.value) < new Date(fromDate)) {
            setToDate("");
        }
    };

    const handleFormatChange = (format) => {
        setSelectedFormat(format);
    };

    // New function to toggle a source tag for an input field group.
    const handleToggleSourceForInputField = (index, sourceLabel) => {
        const updatedFields = [...inputFields];
        if (!updatedFields[index].sources) {
            updatedFields[index].sources = [];
        }
        if (updatedFields[index].sources.includes(sourceLabel)) {
            // Remove the source for this field
            updatedFields[index].sources = updatedFields[index].sources.filter(s => s !== sourceLabel);
            // Check if the source is used in any other field group
            const stillUsed = updatedFields.some((field, idx) =>
                idx !== index && field.sources && field.sources.includes(sourceLabel)
            );
            if (!stillUsed) {
                setCheckboxGroup(prev => ({ ...prev, [sourceLabel]: false }));
            }
        } else {
            // Add the source to the field group
            updatedFields[index].sources.push(sourceLabel);
            if (!checkboxGroup[sourceLabel]) {
                setCheckboxGroup(prev => ({ ...prev, [sourceLabel]: true }));
            }
        }
        setInputFields(updatedFields);
    };

    // Add this new function to check progress
    const checkGenerationProgress = async () => {
        if (!reportId) return;
        
        try {
            setIsCheckingProgress(true);
            const response = await api.get(`/check_report_progress/${reportId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            
            console.log("Progress response:", response.data); // Add logging to debug
            
            if (response.data.status === "in_progress") {
                // Ensure we have valid data before updating state
                const newProgress = {
                    progress: response.data.progress || 0,
                    completedFiles: response.data.completed_files || 0,
                    totalFiles: response.data.total_files || 0,
                    currentStage: response.data.current_stage || "Preparing",
                    currentSubject: response.data.current_subject || "",
                    completedSubjects: response.data.completed_subjects || [],
                    current_metric_datapoints: response.data.current_metric_datapoints || 0
                };
                
                console.log("Updating progress with:", newProgress); // Add logging to debug
                setGenerationProgress(newProgress);
            }
        } catch (error) {
            console.error("Error checking generation progress:", error);
        } finally {
            setIsCheckingProgress(false);
        }
    };

    // Modify handleGenerateReport to remove automatic progress checking
    const handleGenerateReport = async () => {
        setReportGeneratingLoading(true);
        try {
            // Convert files to array of bytes
            const readFileAsBytes = (file) => {
                return new Promise((resolve, reject) => {
                    const reader = new FileReader();
                    reader.readAsArrayBuffer(file);
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = (error) => reject(error);
                });
            };

            const fileContents = await Promise.all(
                uploadedFiles.map(async (item) => {
                    const bytes = await readFileAsBytes(item.file);
                    return {
                        name: item.file.name,
                        subject: item.subject,
                        content: Array.from(new Uint8Array(bytes))
                    };
                })
            );

            const report = {
                type: "custom",
                topics: inputFields.reduce((acc, fields) => {
                    if (fields.subject && fields.description && fields.examples) {
                        acc[fields.subject.toLowerCase()] = {
                            description: fields.description,
                            examples: fields.examples,
                            sources: fields.sources || []
                        };
                    }
                    return acc;
                }, {}),
                uploaded_documents: fileContents,
                date: { from: fromDate, to: toDate },
                sources: Object.keys(checkboxGroup).filter(source => checkboxGroup[source]),
                format: selectedFormat,
            };

            // Trigger report generation
            const response = await api.post("/generate_report", report, {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            console.log("Report generation triggered:", response.data);

            // Save the unique report ID returned by the backend
            const uniqueId = response.data.unique_id;
            setReportId(uniqueId);
            localStorage.setItem('reportId', uniqueId);
            
            // Save all report data to localStorage
            saveReportDataToStorage();

            // Initial progress check
            await checkGenerationProgress();

        } catch (error) {
            console.error("Error generating report:", error);
            localStorage.removeItem('reportId');
            setReportId(null);
            // Clear all report data from localStorage
            clearReportDataFromStorage();
        } finally {
            setReportGeneratingLoading(false);
        }
    };

    // Add cleanup for progress checking interval
    useEffect(() => {
        return () => {
            if (progressCheckInterval) {
                clearInterval(progressCheckInterval);
            }
        };
    }, [progressCheckInterval]);

    const handleDownloadReport = async () => {
        if (!reportId) return;
        try {
            setDownloadInProgress(true);
            setDownloadMessage("");

            // Stop progress checking
            if (progressCheckInterval) {
                clearInterval(progressCheckInterval);
                setProgressCheckInterval(null);
            }

            // Check if the report is complete
            const statusResponse = await api.get(`/check_report_status/${reportId}`, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });

            if (statusResponse.data.status === "complete") {
                // If complete, download the report
                const downloadResponse = await api.get(`/download_report/${reportId}`, {
                    responseType: 'blob',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                });
                const blob = new Blob([downloadResponse.data], { type: 'application/zip' });
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `reports_${reportId}.zip`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                
                // Clear report ID and all report data from localStorage after successful download
                localStorage.removeItem('reportId');
                setReportId(null);
                clearReportDataFromStorage();
            } else if (statusResponse.data.status === "insufficient") {
                // Set a message for insufficient status
                setDownloadMessage("Det gick inte att generera rapporten med den definierade ämnet och utvalda källorna!");
                setTimeout(() => {
                    localStorage.removeItem('reportId');
                    setReportId(null);
                    setDownloadMessage(null);
                    // Clear all report data from localStorage
                    clearReportDataFromStorage();
                }, 3000);
            } else {
                // Set a message for a pending report
                setDownloadMessage("Rapporten är inte klar än. Vänligen försök igen senare.");
            }
        } catch (error) {
            console.error("Error checking report status or downloading:", error);
            setDownloadMessage("Ett fel uppstod vid hämtning av rapporten.");
        } finally {
            setDownloadInProgress(false);
        }
    };

    // Add cancel function
    const handleCancelGeneration = async () => {
        if (!reportId) return;
        
        try {
            setIsCancelling(true);
            await api.post(`/cancel_report/${reportId}`, {}, {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                },
            });
            
            // Clear report data
            localStorage.removeItem('reportId');
            setReportId(null);
            clearReportDataFromStorage();
            
            // Reset progress state
            setGenerationProgress({
                progress: 0,
                completedFiles: 0,
                totalFiles: 0,
                currentStage: "Preparing",
                currentSubject: "",
                completedSubjects: []
            });
            
        } catch (error) {
            console.error("Error cancelling report generation:", error);
        } finally {
            setIsCancelling(false);
        }
    };

    return (
        <Box sx={{ 
            display: "flex", 
            flexDirection: "column", 
            height: "100%",
            width: "100%",
            overflowY: "auto",
            padding: "0 8px",
        }}>
            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Sätter standardvärden framvagnen
                </Typography>
                <Button
                    variant="contained"
                    onClick={handleGenerateForFramvagnen}
                    startIcon={<CheckCircleIcon />}
                    sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark
                        }
                    }}
                >
                    Generera för framvagnen
                </Button>
            </Paper>

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Ange ämnen
                </Typography>
                
                {inputFields.map((fields, index) => (
                    <Paper 
                        key={index} 
                        elevation={0} 
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            border: `1px solid ${theme.palette.primary.main}30`
                        }}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography 
                                variant="subtitle2" 
                                sx={{ 
                                    color: theme.palette.primary.dark,
                                    fontWeight: 600
                                }}
                            >
                                Ämne {index + 1}
                            </Typography>
                            <IconButton 
                                size="small" 
                                onClick={() => handleRemoveInputPair(index)}
                                sx={{ 
                                    color: theme.palette.error.main,
                                    "&:hover": {
                                        backgroundColor: `${theme.palette.error.main}10`
                                    }
                                }}
                            >
                                <RemoveIcon />
                            </IconButton>
                        </Box>
                        
                        <TextField
                            fullWidth
                            label="Ämne"
                            value={fields.subject}
                            onFocus={() => handleFocus(index, "subject")}
                            onBlur={handleBlur}
                            onChange={(e) => handleInputChange(index, "subject", e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Innehållsbeskrivning"
                            value={fields.description}
                            onFocus={() => handleFocus(index, "description")}
                            onBlur={handleBlur}
                            onChange={(e) => handleInputChange(index, "description", e.target.value)}
                            multiline
                            rows={isFocused(index, "description") ? 6 : 3}
                            sx={{ mb: 2 }}
                        />
                        
                        <TextField
                            fullWidth
                            label="Exempel på innehåll"
                            value={fields.examples}
                            onFocus={() => handleFocus(index, "examples")}
                            onBlur={handleBlur}
                            onChange={(e) => handleInputChange(index, "examples", e.target.value)}
                            multiline
                            rows={isFocused(index, "examples") ? 6 : 3}
                            sx={{ mb: 2 }}
                        />
                        
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 1, 
                                color: theme.palette.primary.dark,
                                fontWeight: 500
                            }}
                        >
                            Relaterade källor:
                        </Typography>
                        
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                            {Object.keys(checkboxGroup).map((source) => {
                                if (source === "Uppladdade dokument") return null;
                                const isActive = fields.sources.includes(source);
                                return (
                                    <Chip
                                        key={source}
                                        label={source}
                                        onClick={() => handleToggleSourceForInputField(index, source)}
                                        color={isActive ? "primary" : "default"}
                                        variant={isActive ? "filled" : "outlined"}
                                        sx={{ 
                                            borderColor: theme.palette.primary.main,
                                            color: isActive ? "#fff" : theme.palette.primary.dark,
                                            "&:hover": {
                                                backgroundColor: isActive ? theme.palette.primary.dark : `${theme.palette.primary.main}10`
                                            }
                                        }}
                                    />
                                );
                            })}
                        </Box>
                    </Paper>
                ))}
                
                <Button
                    variant="outlined"
                    onClick={handleAddInputPair}
                    startIcon={<AddIcon />}
                    sx={{ 
                        borderColor: theme.palette.primary.main,
                        color: theme.palette.primary.dark,
                        "&:hover": {
                            borderColor: theme.palette.primary.dark,
                            backgroundColor: `${theme.palette.primary.main}10`
                        }
                    }}
                >
                    Lägg till ämne
                </Button>
            </Paper>

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Ladda upp dokument
                </Typography>
                
                {subjectModalVisible && (
                    <Paper 
                        elevation={3} 
                        sx={{ 
                            p: 2, 
                            mb: 2, 
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            position: "relative",
                            zIndex: 10
                        }}
                    >
                        <Typography 
                            variant="subtitle2" 
                            sx={{ 
                                mb: 1, 
                                color: theme.palette.primary.dark,
                                fontWeight: 600
                            }}
                        >
                            Välj ämne för filen
                        </Typography>
                        
                        <TextField
                            select
                            fullWidth
                            value={selectedSubjectForPendingFile}
                            onChange={(e) => {
                                setSelectedSubjectForPendingFile(e.target.value);
                                setSubjectError("");
                            }}
                            error={!!subjectError}
                            helperText={subjectError}
                            sx={{ mb: 2 }}
                            SelectProps={{
                                native: true,
                            }}
                        >
                            <option value="">--Välj ett ämne till filen--</option>
                            {inputFields
                                .filter((field) => field.subject.trim() !== "")
                                .map((field, index) => (
                                    <option key={index} value={field.subject}>
                                        {field.subject}
                                    </option>
                                ))}
                        </TextField>
                        
                        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                            <Button
                                variant="outlined"
                                onClick={handleCancelFileMapping}
                                sx={{ 
                                    borderColor: theme.palette.error.main,
                                    color: theme.palette.error.main,
                                    "&:hover": {
                                        borderColor: theme.palette.error.dark,
                                        backgroundColor: `${theme.palette.error.main}10`
                                    }
                                }}
                            >
                                Stäng
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleConfirmFileMapping}
                                sx={{ 
                                    backgroundColor: theme.palette.primary.main,
                                    "&:hover": {
                                        backgroundColor: theme.palette.primary.dark
                                    }
                                }}
                            >
                                Bekräfta
                            </Button>
                        </Box>
                    </Paper>
                )}
                
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ 
                            mr: 1,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.dark,
                            "&:hover": {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: `${theme.palette.primary.main}10`
                            }
                        }}
                    >
                        Välj en fil
                        <input
                            type="file"
                            hidden
                            onChange={handleFileChange}
                        />
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleFileUpload}
                        disabled={!fileInput}
                        sx={{ 
                            backgroundColor: theme.palette.primary.main,
                            "&:hover": {
                                backgroundColor: theme.palette.primary.dark
                            }
                        }}
                    >
                        Ladda upp
                    </Button>
                </Box>
                
                {fileInput && (
                    <Typography 
                        variant="body2" 
                        sx={{ 
                            mb: 2, 
                            color: theme.palette.primary.dark,
                            fontStyle: "italic"
                        }}
                    >
                        Vald fil: {fileInput.name}
                    </Typography>
                )}
                
                {uploadedFiles.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 1, 
                                color: theme.palette.primary.dark,
                                fontWeight: 500
                            }}
                        >
                            Uppladdade filer:
                        </Typography>
                        
                        <List sx={{ p: 0 }}>
                            {uploadedFiles.map((item, index) => (
                                <ListItem 
                                    key={index} 
                                    sx={{ 
                                        py: 1,
                                        px: 0,
                                        "&:hover": {
                                            backgroundColor: `${theme.palette.primary.main}05`
                                        }
                                    }}
                                >
                                    <ListItemText 
                                        primary={item.file.name} 
                                        secondary={`Ämne: ${item.subject}`}
                                        primaryTypographyProps={{
                                            sx: { 
                                                color: theme.palette.primary.dark,
                                                fontWeight: 500
                                            }
                                        }}
                                        secondaryTypographyProps={{
                                            sx: { 
                                                color: theme.palette.text.secondary,
                                                fontSize: "0.8rem"
                                            }
                                        }}
                                    />
                                    <ListItemSecondaryAction>
                                        <IconButton 
                                            edge="end" 
                                            onClick={() => handleDeleteFile(item)}
                                            sx={{ 
                                                color: theme.palette.error.main,
                                                "&:hover": {
                                                    backgroundColor: `${theme.palette.error.main}10`
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </ListItemSecondaryAction>
                                </ListItem>
                            ))}
                        </List>
                    </Box>
                )}
            </Paper>

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Datumintervall
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <Box>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 1, 
                                color: theme.palette.primary.dark,
                                fontWeight: 500
                            }}
                        >
                            Från datum
                        </Typography>
                        <TextField
                            type="date"
                            value={fromDate}
                            onChange={handleFromDateChange}
                            fullWidth
                            InputProps={{
                                startAdornment: <CalendarTodayIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />,
                            }}
                        />
                    </Box>
                    
                    <Box>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                mb: 1, 
                                color: theme.palette.primary.dark,
                                fontWeight: 500
                            }}
                        >
                            Till datum
                        </Typography>
                        <TextField
                            type="date"
                            value={toDate}
                            onChange={handleToDateChange}
                            fullWidth
                            InputProps={{
                                startAdornment: <CalendarTodayIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />,
                            }}
                        />
                    </Box>
                </Box>
            </Paper>

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Välj källor
                </Typography>
                
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {Object.keys(checkboxGroup).map((label) => (
                        <FormControlLabel
                            key={label}
                            control={
                                <Checkbox 
                                    checked={checkboxGroup[label]} 
                                    onChange={() => handleCheckboxChange(label)}
                                    sx={{ 
                                        color: theme.palette.primary.main,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            }
                            label={label}
                            sx={{ 
                                color: theme.palette.primary.dark,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: '0.9rem',
                                }
                            }}
                        />
                    ))}
                </Box>
            </Paper>

            <Paper 
                elevation={0} 
                sx={{ 
                    p: 2, 
                    mb: 2, 
                    backgroundColor: `${theme.palette.primary.main}10`,
                    borderRadius: "8px",
                    border: `1px solid ${theme.palette.primary.main}30`
                }}
            >
                <Typography 
                    variant="subtitle1" 
                    sx={{ 
                        mb: 2, 
                        color: theme.palette.primary.dark,
                        fontWeight: 600
                    }}
                >
                    Välj filformat
                </Typography>
                
                <FormControl component="fieldset">
                    <RadioGroup
                        value={selectedFormat}
                        onChange={(e) => handleFormatChange(e.target.value)}
                    >
                        <FormControlLabel 
                            value="PDF" 
                            control={
                                <Radio 
                                    sx={{ 
                                        color: theme.palette.primary.main,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            } 
                            label="PDF" 
                            sx={{ color: theme.palette.primary.dark }}
                        />
                        <FormControlLabel 
                            value="DOCX" 
                            control={
                                <Radio 
                                    sx={{ 
                                        color: theme.palette.primary.main,
                                        '&.Mui-checked': {
                                            color: theme.palette.primary.main,
                                        },
                                    }}
                                />
                            } 
                            label="DOCX" 
                            sx={{ color: theme.palette.primary.dark }}
                        />
                    </RadioGroup>
                </FormControl>
            </Paper>

            <Box sx={{ mt: "auto", pt: 2 }}>
                <Button
                    variant="contained"
                    onClick={handleGenerateReport}
                    disabled={reportGeneratingLoading || reportId}
                    startIcon={reportGeneratingLoading ? <CircularProgress size={20} color="inherit" /> : null}
                    fullWidth
                    sx={{ 
                        backgroundColor: theme.palette.primary.main,
                        "&:hover": {
                            backgroundColor: theme.palette.primary.dark
                        },
                        "&.Mui-disabled": {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.action.disabled,
                        }
                    }}
                >
                    {reportGeneratingLoading ? "Genererar..." : reportId ? "Rapport genereras..." : "Generera rapport"}
                </Button>
                
                {reportId && (
                    <Box sx={{ mt: 2 }}>
                        {/* Update progress display */}
                        <Box sx={{ 
                            mb: 2, 
                            p: 2, 
                            borderRadius: 1,
                            backgroundColor: `${theme.palette.info.main}15`,
                            border: `1px solid ${theme.palette.info.main}`
                        }}>
                            <Typography variant="body2" sx={{ mb: 1, color: theme.palette.info.dark, fontWeight: 500 }}>
                                {generationProgress.currentStage}
                            </Typography>
                            {generationProgress.currentSubject && (
                                <Typography variant="body2" sx={{ mb: 1, color: theme.palette.info.dark, fontSize: '0.9rem' }}>
                                    Ämne: {generationProgress.currentSubject}
                                </Typography>
                            )}
                            {generationProgress.current_metric_datapoints > 0 && (
                                <Typography variant="body2" sx={{ mb: 1, color: theme.palette.info.dark, fontSize: '0.9rem' }}>
                                    Bearbetar {generationProgress.current_metric_datapoints} datapunkter
                                </Typography>
                            )}
                            <Typography variant="body2" sx={{ mb: 1, color: theme.palette.info.dark, fontSize: '0.8rem' }}>
                                Genererar rapporter: {generationProgress.completedFiles} av {generationProgress.totalFiles} färdiga
                            </Typography>
                            {generationProgress.completedSubjects.length > 0 && (
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" sx={{ color: theme.palette.info.dark, fontSize: '0.8rem', mb: 0.5 }}>
                                        Färdiga ämnen:
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {generationProgress.completedSubjects.map((subject, index) => (
                                            <Chip
                                                key={index}
                                                label={subject}
                                                size="small"
                                                sx={{
                                                    backgroundColor: theme.palette.success.light,
                                                    color: theme.palette.success.dark,
                                                    fontSize: '0.7rem',
                                                    height: '24px'
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </Box>
                            )}
                            <Box sx={{ width: '100%', position: 'relative' }}>
                                <LinearProgress 
                                    variant="determinate" 
                                    value={generationProgress.progress}
                                    sx={{
                                        height: 8,
                                        borderRadius: 4,
                                        backgroundColor: `${theme.palette.info.main}30`,
                                        '& .MuiLinearProgress-bar': {
                                            backgroundColor: theme.palette.info.main
                                        }
                                    }}
                                />
                                <Typography 
                                    variant="caption" 
                                    sx={{ 
                                        position: 'absolute', 
                                        right: 0, 
                                        top: -20,
                                        color: theme.palette.info.dark
                                    }}
                                >
                                    {Math.round(generationProgress.progress)}%
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleDownloadReport}
                                    disabled={downloadInProgress}
                                    startIcon={downloadInProgress ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                                    sx={{ 
                                        flex: 4, // 80% width
                                        backgroundColor: theme.palette.primary.main,
                                        "&:hover": {
                                            backgroundColor: theme.palette.primary.dark
                                        },
                                        "&.Mui-disabled": {
                                            backgroundColor: theme.palette.action.disabledBackground,
                                            color: theme.palette.action.disabled,
                                        }
                                    }}
                                >
                                    {downloadInProgress ? "Kontrollerar..." : "Ladda ner rapport"}
                                </Button>
                                
                                <IconButton
                                    onClick={checkGenerationProgress}
                                    disabled={isCheckingProgress}
                                    sx={{ 
                                        flex: 1, // 20% width
                                        border: `1px solid ${theme.palette.info.main}`,
                                        color: theme.palette.info.main,
                                        "&:hover": {
                                            borderColor: theme.palette.info.dark,
                                            backgroundColor: `${theme.palette.info.main}10`
                                        }
                                    }}
                                >
                                    {isCheckingProgress ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                                </IconButton>
                            </Box>
                            
                            {generationProgress.progress < 100 && (
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelGeneration}
                                    disabled={isCancelling}
                                    startIcon={isCancelling ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                                    fullWidth
                                    sx={{ 
                                        borderColor: theme.palette.error.main,
                                        color: theme.palette.error.main,
                                        "&:hover": {
                                            borderColor: theme.palette.error.dark,
                                            backgroundColor: `${theme.palette.error.main}10`
                                        }
                                    }}
                                >
                                    {isCancelling ? "Avbryter..." : "Avbryt generering"}
                                </Button>
                            )}
                        </Box>
                        
                        {downloadMessage && (
                            <Box 
                                sx={{ 
                                    mt: 2, 
                                    p: 2, 
                                    borderRadius: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    backgroundColor: downloadMessage.includes("inte klar") 
                                        ? `${theme.palette.info.main}15` 
                                        : `${theme.palette.error.main}15`,
                                    color: downloadMessage.includes("inte klar") 
                                        ? theme.palette.info.dark 
                                        : theme.palette.error.dark,
                                    border: `1px solid ${downloadMessage.includes("inte klar") 
                                        ? theme.palette.info.main 
                                        : theme.palette.error.main}`
                                }}
                            >
                                {downloadMessage.includes("inte klar") ? (
                                    <CircularProgress size={20} sx={{ mr: 1 }} />
                                ) : (
                                    <ErrorIcon sx={{ mr: 1 }} />
                                )}
                                <Typography variant="body2">
                                    {downloadMessage}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default ConfigurationPanel;
